/**
 * NoResultsState — F-006 Phase 5 (IMPL-P5-005).
 *
 * Rendered ONLY when `status === 'no-results'`: the user has pratiche, but
 * the active search/filters return zero rows. Search bar + filters stay
 * visible above this component (handled by the page composition).
 *
 * Two API shapes:
 *
 *  1. Controlled (preferred for tests):
 *     `<NoResultsState searchTerm onClearFilters hasActiveFilters />`
 *  2. Provider-driven (default in the page):
 *     `<NoResultsState />` — reads/writes via `useArchivio()`.
 */
import { SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useArchivio } from '../hooks/useArchivio';

interface NoResultsStateProps {
  searchTerm?: string;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

export function NoResultsState(props: NoResultsStateProps) {
  // Hook is always called (Rules of Hooks) — we use its values only when the
  // corresponding prop is missing.
  let ctx: ReturnType<typeof useArchivio> | null = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    ctx = useArchivio();
  } catch {
    // Outside provider (e.g. isolated test) — fully rely on props.
    ctx = null;
  }

  const searchTerm = props.searchTerm ?? ctx?.searchTerm ?? '';
  const hasActiveFilters = props.hasActiveFilters ?? ctx?.hasActiveFilters ?? false;
  const onClearFilters = props.onClearFilters ?? ctx?.clearFilters ?? (() => {});

  return (
    <div
      className="flex flex-col items-center justify-center gap-4 rounded-lg border bg-muted/20 px-6 py-12 text-center"
      data-testid="no-results-state"
    >
      <SearchX className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
      <div className="space-y-2">
        <h2 className="text-base font-semibold">Nessuna pratica trovata</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          {searchTerm ? (
            <>
              Nessun risultato per <span className="font-medium">"{searchTerm}"</span>
              {hasActiveFilters ? ' con i filtri attivi' : ''}.
            </>
          ) : (
            <>Nessun risultato con i filtri attivi.</>
          )}{' '}
          Prova a modificare la ricerca o azzerare i filtri.
        </p>
      </div>
      <Button type="button" variant="outline" onClick={onClearFilters}>
        Azzera filtri
      </Button>
    </div>
  );
}
